import type { BannerSpec } from '@invideoio/web-shared/components/WebinarBanner/banner-specs';

/**
 * Resolves the announcement banner slides for the top-of-page banner.
 *
 * Three distinct outcomes, all meaningful:
 * - `BannerSpec[]` — slides to show. Note this also covers the no-CMS-configured
 *   case: with the CMS env vars unset the shared module returns its hardcoded
 *   BANNERS array, so local/unconfigured environments still get the default bar.
 * - `null` — the CMS is configured but never answered (cold outage), or the SSR
 *   payload had no value. The banner component renders its hardcoded defaults.
 * - `[]` — a definitive "no bar right now" from the CMS. The banner component
 *   renders nothing.
 *
 * On the server this hits the CMS (behind the shared module's 5-minute in-process
 * cache). In the browser there is nothing to fetch: `/api/*` on the public origin
 * routes to pro-landing-pages, not to this app, so we read the value SSR already
 * embedded in `__NEXT_DATA__` instead.
 */
export const getAnnouncementBanners = async (): Promise<
  BannerSpec[] | null
> => {
  if (typeof window === 'undefined') {
    const { fetchAnnouncementBanners } = await import(
      '@invideoio/web-shared/lib/API/announcement-bar'
    );
    return fetchAnnouncementBanners();
  }

  // `__NEXT_DATA__.props` is typed as `Record<string, any>` by Next, so narrow it.
  const props = window.__NEXT_DATA__?.props as
    | { announcementBanners?: BannerSpec[] | null }
    | undefined;

  return props?.announcementBanners ?? null;
};
